import {parseAxiosPromise} from '@/axiosInstance'
import {QUERY_KEYS} from '@/queryClient'
import {PaginationResponse, ResponseType} from '@/types'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {z} from 'zod'
import {httpGetDiseases, HttpGetTreatmentRequestsDiseasesOptions} from './treatment-requests.http'
import {captureException} from '@sentry/react'
import {DiseaseExtended, DiseaseSmall} from '@/features/treatment-requests/types'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'

const ResponseShape = z.object({
    small: z.object({
        data: DiseaseSmall.array(),
        meta: PaginationResponse
    }),
    extended: z.object({
        data: DiseaseExtended.array(),
        meta: PaginationResponse
    })
})
type ResponseShape = z.infer<typeof ResponseShape>

export const useDiseases = <TResponseType extends ResponseType>(
    params: Omit<HttpGetTreatmentRequestsDiseasesOptions['params'], 'page'> & {responseType: TResponseType},
    options?: Pick<UseInfiniteQueryOptions<ResponseShape[TResponseType]>, 'enabled'>
) => {
    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.DISEASES, params],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetDiseases({
                    params: {
                        page: pageParam,
                        ...params
                    }
                }),
                onZodError: captureException,
                responseShape: ResponseShape.shape[params.responseType]
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: (query.data?.pages[0].data.length
            ? query.data.pages.flatMap(page => page.data)
            : []) as ResponseShape[TResponseType]['data']
    }
}
